import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  ButtonWithLoader,
  PuiTextField,
  TextInteractive,
} from '@pbt/pbt-ui-components'

import ClientCard from '~/components/common/ContactTargetSlot/ClientCard'
import PatientCard from '~/components/common/ContactTargetSlot/PatientCard'
import UserSelect, {
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import AddedToAppointmentLabel from '~/components/common/labels/AddedToAppointmentLabel'
import Expander from '~/components/common/lists/Expander'
import NotesTemplateInput from '~/components/common/NotesTemplateInput'
import { ImagingOrderStatus } from '~/constants/imaging'
import { OrderType } from '~/constants/SOAPStates'
import { isFieldValuesChanged } from '~/utils'

import ImagingStatusSelect from '../../imaging-procedures/ImagingStatusSelect'
import ModalitySelect from '../../imaging-procedures/ModalitySelect'
import { useImagingDetails } from './useImagingDetails'

const useStyles = makeStyles(
  (theme) => ({
    contactContainer: {
      border: theme.constants.tableBorder,
    },
  }),
  { name: 'ImagingDetails' },
)

export interface ImagingDetailsProps {
  item: string
  onClose: () => void
}

const ImagingDetails = ({ item: itemId, onClose }: ImagingDetailsProps) => {
  const classes = useStyles()
  const { t } = useTranslation('Common')

  const {
    isSaving,
    isFetching,
    isLoadingDashboardRecord,
    clientId,
    patientId,
    order,
    appointment,
    vendorId,
    status,
    fields,
    saveOrder: handleSaveOrder,
    viewSoap,
    markAsReviewed,
    viewImages: handleViewImages,
    openInvoice: handleOpenInvoice,
  } = useImagingDetails(itemId, onClose)

  const { name, modalityId, assignedVetId, notes, statusId } = fields

  const saveOrder = () => {
    handleSaveOrder()
  }

  const viewImages = () => {
    handleViewImages()
  }

  const backToDashboard = () => onClose()

  return (
    <Expander
      additionalButtons={[
        status === ImagingOrderStatus.IMAGE_RECEIVED && (
          <ButtonWithLoader
            disabled={isSaving || isLoadingDashboardRecord}
            key="view-invoice"
            onClick={markAsReviewed}
          >
            {t('Common:MARK_AS_REVIEWED')}
          </ButtonWithLoader>
        ),
        (status === ImagingOrderStatus.IMAGE_RECEIVED ||
          status === ImagingOrderStatus.COMPLETED) &&
          Boolean(order?.resultsUrl) && (
            <ButtonWithLoader
              disabled={isSaving || isLoadingDashboardRecord}
              key="view-invoice"
              onClick={viewImages}
            >
              {t('Common:VIEW_IMAGES')}
            </ButtonWithLoader>
          ),
        Boolean(order?.soapId) && !order?.invoiceId && (
          <ButtonWithLoader
            disabled={isSaving || isLoadingDashboardRecord}
            key="view-soap"
            onClick={viewSoap}
          >
            {t('Common:VIEW_SOAP')}
          </ButtonWithLoader>
        ),
        Boolean(order?.invoiceId) && (
          <ButtonWithLoader
            disabled={isSaving || isLoadingDashboardRecord}
            key="view-invoice"
            onClick={() =>
              handleOpenInvoice({
                clientId,
                patientId,
                invoiceId: order?.invoiceId,
                soapId: order?.soapId,
                logId: order?.logId,
                logType: OrderType.PROCEDURE,
              })
            }
          >
            {t('Common:VIEW_INVOICE')}
          </ButtonWithLoader>
        ),
      ]}
      expandedItemClass="imaging-request"
      getUnsavedData={() => isFieldValuesChanged(fields)}
      isFetching={isFetching}
      isSaving={isSaving || isLoadingDashboardRecord}
      onBack={backToDashboard}
      onSaveRequested={saveOrder}
    >
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <TextInteractive
            isLoading={isFetching}
            loaderHeight={30}
            variant="h3"
          >
            {order?.name}
          </TextInteractive>
        </Grid>
        <Grid container item>
          <ImagingStatusSelect
            field={statusId}
            isSoapFinalized={Boolean(order?.soapFinalized)}
          />
        </Grid>
        <Grid item className={classes.contactContainer} m={1} p={2}>
          <Grid container spacing={1}>
            {clientId && patientId && (
              <Grid item xs>
                <PatientCard clientId={clientId} patientId={patientId} />
              </Grid>
            )}
            {clientId && (
              <Grid item xs>
                <ClientCard clientId={clientId} />
              </Grid>
            )}
          </Grid>
        </Grid>
        {appointment && order?.soapId && (
          <Grid item>
            <AddedToAppointmentLabel
              appointment={appointment}
              soapId={order?.soapId}
            />
          </Grid>
        )}
        <Grid item>
          <UserSelect
            readOnly
            field={assignedVetId}
            filterScope={UserSelectFilterScope.Doctors}
          />
        </Grid>
        <Grid container item spacing={2}>
          <Grid container item xs={7}>
            <PuiTextField field={name} label={name.label} margin="none" />
          </Grid>
          <Grid item xs={5}>
            <ModalitySelect field={modalityId} vendorId={vendorId} />
          </Grid>
        </Grid>
        <Grid container item>
          <NotesTemplateInput
            hidePanel
            resetStateOnValueChange
            field={notes}
            minHeight={2}
            placeholder={notes.label}
          />
        </Grid>
      </Grid>
    </Expander>
  )
}

export default ImagingDetails
